/**
 * maintenance_page_v3.js — DAS-109 Phase 07: Maintenance V3 · 캘린더 히어로 + 자동화 규칙
 *
 * 원본 시안: maintenance.jsx::MaintenanceDesktopV2 / MaintenanceMobileV2
 * UI: 캘린더 히어로(월간 그리드) + 다가오는 점검 목록 + 거래소별 빈도 + 자동화 규칙 placeholder
 *
 * 재사용 컴포넌트:
 *   - components/my_page/maintenance_card.js (MaintenanceCard) — CRUD 로직 전체 재사용
 *     onSchedulesChanged prop 포함 — 캘린더 즉시 동기화 (DAS-66 패턴)
 *
 * BE gap G3:
 *   - 캘린더 히어로: 기존 점검 리스트를 월별 그리드로 FE 렌더 (listSchedules 결과 활용)
 *   - 자동화 규칙: BE 미제공 → placeholder 카드 + TODO(DAS-65-G3)
 *   - 거래소별 빈도: 기존 리스트에서 FE 집계
 *
 * silver 토큰 전용. JetBrains Mono 유지. 하드코딩 색상(#hex, rgba) 금지.
 */

import React from 'react';
import dayjs from 'dayjs';
import NavBar from '../common/nav_bar';
import MaintenanceBanner from '../common/maintenance_banner';
import ScrollToTopButton from '../common/scroll_to_top_button';
import MaintenanceCard from '../my_page/maintenance_card';
import { listSchedules } from '../../services/maintenanceApi';
import { getNavCollapsed, setNavCollapsed } from '../../utils/navState';
import { isMobileViewport } from '../../utils/responsive';
import { V2Icon, KpiLabel, V2Badge, ExchangeChip, Card, Btn } from '../common/claude_primitives';

/* ── 날짜 헬퍼 ───────────────────────────────────────────── */
const KST_OFFSET = 9 * 60; // 분

const toKSTDayjs = (isoStr) => dayjs(isoStr).utcOffset(KST_OFFSET);

const classifySchedule = (item) => {
    const now = new Date();
    const start = new Date(item.start_at);
    const end = new Date(item.end_at);
    if (end <= now) return 'past';
    if (start <= now) return 'ongoing';
    return 'upcoming';
};

/* ── 다음 점검까지 카운트다운 (ms → "N일 N시간") ─────────── */
const formatCountdown = (ms) => {
    if (ms <= 0) return '진행 중';
    const totalMin = Math.floor(ms / 60000);
    const days = Math.floor(totalMin / (60 * 24));
    const hours = Math.floor((totalMin % (60 * 24)) / 60);
    const mins = totalMin % 60;
    if (days > 0) return `${days}일 ${hours}시간`;
    if (hours > 0) return `${hours}시간 ${mins}분`;
    return `${mins}분`;
};

/* ── 거래소별 점검 빈도 FE 집계 ──────────────────────────── */
const buildExchangeFrequency = (schedules) => {
    const map = new Map();
    schedules.forEach((s) => {
        const ex = (s.exchange || '').toLowerCase();
        if (!ex) return;
        const start = new Date(s.start_at);
        const end = new Date(s.end_at);
        const hours = (end - start) / 3600000;
        const cur = map.get(ex) || { count: 0, hours: 0 };
        map.set(ex, { count: cur.count + 1, hours: cur.hours + hours });
    });
    return Array.from(map.entries())
        .map(([ex, v]) => ({ ex: ex.toUpperCase(), count: v.count, hours: v.hours.toFixed(1) }))
        .sort((a, b) => b.count - a.count);
};

/* ── 월간 캘린더 그리드 ────────────────────────────────────── */
function MonthlyCalendar({ schedules, year, month }) {
    // month: 1-indexed
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const daysInMonth = lastDay.getDate();

    // 월요일 시작: 0=월, 6=일
    let startDow = firstDay.getDay(); // 0=일
    startDow = startDow === 0 ? 6 : startDow - 1; // 월 기준으로 전환

    // 날짜별 점검 항목 매핑
    const dayMarkers = {};
    schedules.forEach((s) => {
        const d = toKSTDayjs(s.start_at);
        if (d.year() === year && d.month() + 1 === month) {
            const day = d.date();
            if (!dayMarkers[day]) dayMarkers[day] = [];
            dayMarkers[day].push(s);
        }
    });

    const today = new Date();
    const todayDay = today.getFullYear() === year && today.getMonth() + 1 === month ? today.getDate() : -1;

    const cells = [];
    // 빈 셀 (앞)
    for (let i = 0; i < startDow; i++) {
        cells.push(null);
    }
    for (let d = 1; d <= daysInMonth; d++) {
        cells.push(d);
    }

    const DOW_LABELS = ['월', '화', '수', '목', '금', '토', '일'];

    return (
        <div>
            {/* 요일 헤더 */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 4 }}>
                {DOW_LABELS.map((d) => (
                    <div key={d} style={{
                        textAlign: 'center', fontSize: 10,
                        color: 'var(--color-content-muted)',
                        fontFamily: 'JetBrains Mono, monospace',
                        letterSpacing: '0.08em', padding: '4px 0',
                    }}>
                        {d}
                    </div>
                ))}
            </div>
            {/* 날짜 셀 */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
                {cells.map((day, idx) => {
                    if (day === null) {
                        return <div key={`empty-${idx}`} />;
                    }
                    const markers = dayMarkers[day] || [];
                    const isToday = day === todayDay;
                    const isPast = new Date(year, month - 1, day) < new Date(today.getFullYear(), today.getMonth(), today.getDate());
                    return (
                        <div
                            key={day}
                            style={{
                                aspectRatio: '1 / 1.1',
                                background: isToday
                                    ? 'var(--color-brand-subtle)'
                                    : 'var(--color-surface-card-alt, var(--color-surface-elevated))',
                                border: `1px solid ${isToday
                                    ? 'color-mix(in oklch, var(--color-brand) 50%, var(--color-surface-border))'
                                    : 'var(--color-surface-border)'}`,
                                borderRadius: 8,
                                padding: 6,
                                display: 'flex', flexDirection: 'column', gap: 3,
                                opacity: isPast ? 0.45 : 1,
                                minWidth: 0,
                            }}
                        >
                            <div style={{
                                fontFamily: 'JetBrains Mono, monospace',
                                fontSize: 11, fontWeight: 600,
                                color: markers.length > 0 ? 'var(--color-content)' : 'var(--color-content-muted)',
                            }}>
                                {day}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 'auto' }}>
                                {markers.slice(0, 2).map((s, i) => {
                                    const cls = classifySchedule(s);
                                    const color = cls === 'ongoing'
                                        ? 'var(--color-loss)'
                                        : cls === 'upcoming'
                                            ? 'var(--color-warning)'
                                            : 'var(--color-content-muted)';
                                    return (
                                        <div key={i} style={{
                                            fontSize: 12, padding: '1px 4px', borderRadius: 3,
                                            background: 'var(--color-surface-elevated)',
                                            border: '1px solid var(--color-surface-border)',
                                            fontFamily: 'JetBrains Mono, monospace',
                                            letterSpacing: '0.03em',
                                            color,
                                            overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                                        }}>
                                            {(s.exchange || '').toUpperCase()}
                                        </div>
                                    );
                                })}
                                {markers.length > 2 && (
                                    <div style={{
                                        fontSize: 12,
                                        color: 'var(--color-content-muted)',
                                        fontFamily: 'JetBrains Mono, monospace',
                                    }}>
                                        +{markers.length - 2}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/* ── 거래소별 빈도 행 ─────────────────────────────────────── */
function FreqRow({ ex, count, hours, maxCount }) {
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 0',
            borderBottom: '1px solid var(--color-surface-border)',
        }}>
            <ExchangeChip name={ex} />
            <div style={{ flex: 1, height: 6, background: 'var(--color-surface-elevated)', borderRadius: 3 }}>
                <div style={{
                    height: '100%',
                    width: `${Math.round((count / maxCount) * 100)}%`,
                    background: 'var(--color-brand)',
                    opacity: 0.65,
                    borderRadius: 3,
                }} />
            </div>
            <span style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 11, fontWeight: 600,
                width: 28, textAlign: 'right',
                color: 'var(--color-content)',
            }}>
                {count}
            </span>
            <span style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 10,
                width: 44, textAlign: 'right',
                color: 'var(--color-content-muted)',
            }}>
                {hours}h
            </span>
        </div>
    );
}

/* ── 자동화 규칙 placeholder ──────────────────────────────── */
// TODO(DAS-65-G3): 자동화 규칙 — BE 미제공. 후속 릴리스 예정.
function AutomationRulesPlaceholder() {
    return (
        <Card padding={18}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-content)' }}>
                    자동화 규칙
                </div>
                <V2Icon name="more" size={16} />
            </div>
            {/* TODO(DAS-65-G3): 자동화 규칙은 BE 미제공 — 후속 릴리스에서 제공됩니다 */}
            <div style={{
                padding: '28px 0',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
                border: '1px dashed var(--color-surface-border)',
                borderRadius: 12,
                background: 'var(--color-surface-elevated)',
            }}>
                <V2Icon name="refresh" size={22} />
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-content)' }}>
                    자동화 규칙
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-content-muted)', textAlign: 'center', maxWidth: 240 }}>
                    자동화 규칙은 후속 릴리스에서 제공됩니다
                </div>
                <V2Badge tone="neutral">Coming Soon</V2Badge>
            </div>
        </Card>
    );
}

/* ── 데스크톱 V2 레이아웃 ─────────────────────────────────── */
function MaintenanceDesktopV2({ schedules, loading, calYear, calMonth, onPrevMonth, onNextMonth, onSchedulesChanged, onOpenCreateModal, maintenanceCardRef }) {
    const upcomingSchedules = schedules
        .filter((s) => classifySchedule(s) === 'upcoming')
        .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));

    const nextSchedule = upcomingSchedules[0] || null;
    const countdownMs = nextSchedule ? new Date(nextSchedule.start_at) - new Date() : 0;

    const freqData = buildExchangeFrequency(schedules);
    const maxCount = freqData.length > 0 ? freqData[0].count : 1;

    const scheduledCount = schedules.filter((s) => classifySchedule(s) === 'upcoming').length;
    const ongoingCount   = schedules.filter((s) => classifySchedule(s) === 'ongoing').length;
    const completedCount = schedules.filter((s) => classifySchedule(s) === 'past').length;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* 시안 V2 페이지 헤더 — breadcrumb + 제목 + subtitle + 새 점검 버튼 */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                <div>
                    <div style={{
                        fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase',
                        color: 'var(--color-content-muted)', fontFamily: 'JetBrains Mono, monospace',
                        marginBottom: 4,
                    }}>
                        시스템 운영 / 점검 캘린더
                    </div>
                    <h1 style={{
                        fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em',
                        color: 'var(--color-content)', margin: 0,
                    }}>
                        Maintenance
                    </h1>
                    <div style={{ fontSize: 12, color: 'var(--color-content-secondary)', marginTop: 4 }}>
                        거래소 점검 · 자동 일시정지 · 복구 플레이북
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                    <span style={{
                        fontFamily: 'JetBrains Mono, monospace', fontSize: 11,
                        color: 'var(--color-content-muted)',
                    }}>
                        예정 {scheduledCount} · 진행 {ongoingCount} · 완료 {completedCount}
                    </span>
                    {/* 시안 V2: 새 점검 버튼 */}
                    <Btn variant="primary" size="sm" icon="plus" type="button" onClick={onOpenCreateModal}>
                        새 점검
                    </Btn>
                </div>
            </div>

            {/* 캘린더 히어로 — 시안 V2: gradient 배경 + 월/연 표시 + 다음 점검 카운트다운 + 컨트롤 */}
            <div
                style={{
                    padding: 22,
                    background: 'linear-gradient(135deg, var(--color-surface-card), var(--color-surface-elevated))',
                    border: '1px solid var(--color-surface-border)',
                    borderRadius: 16,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                    <div>
                        <div style={{
                            fontSize: 11, letterSpacing: '0.14em',
                            color: 'var(--color-content-muted)',
                            fontFamily: 'JetBrains Mono, monospace',
                            textTransform: 'uppercase', marginBottom: 6,
                        }}>
                            {calMonth}월 · {calYear}
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--color-content)' }}>
                            {nextSchedule
                                ? `다음 점검까지 ${formatCountdown(countdownMs)}`
                                : '예정된 점검 없음'}
                        </div>
                        {nextSchedule && (
                            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <ExchangeChip name={nextSchedule.exchange} />
                                <span style={{
                                    fontFamily: 'JetBrains Mono, monospace', fontSize: 11,
                                    color: 'var(--color-content-secondary)',
                                }}>
                                    {toKSTDayjs(nextSchedule.start_at).format('MM-DD HH:mm')} → {toKSTDayjs(nextSchedule.end_at).format('HH:mm')} KST
                                </span>
                            </div>
                        )}
                    </div>
                    {/* 시안 V2: ◀ 월간 주간 ▶ 토글 pill */}
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <button
                            type="button"
                            onClick={onPrevMonth}
                            className="rounded-lg border border-surface-border bg-surface-card px-2 py-1 text-xs text-content-secondary hover:bg-surface-hover transition-colors"
                            aria-label="이전 달"
                        >
                            <V2Icon name="chevL" size={12} />
                        </button>
                        {/* 월간 활성 — 주간은 P2 후속 (DAS-73 sub-phase) 으로 disabled */}
                        <span
                            aria-pressed="true"
                            className="rounded-full border border-brand/40 bg-brand/15 px-3 py-1 text-xs font-medium text-brand-light"
                        >
                            월간
                        </span>
                        <span
                            aria-pressed="false"
                            aria-disabled="true"
                            title="주간 뷰는 후속 릴리스에서 제공됩니다"
                            className="rounded-full border border-surface-border bg-surface-card px-3 py-1 text-xs text-content-muted cursor-not-allowed opacity-60"
                        >
                            주간
                        </span>
                        <button
                            type="button"
                            onClick={onNextMonth}
                            className="rounded-lg border border-surface-border bg-surface-card px-2 py-1 text-xs text-content-secondary hover:bg-surface-hover transition-colors"
                            aria-label="다음 달"
                        >
                            <V2Icon name="chevR" size={12} />
                        </button>
                    </div>
                </div>
                <MonthlyCalendar schedules={schedules} year={calYear} month={calMonth} />
            </div>

            {/* 2컬럼: 점검 목록 + 사이드 패널 */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 14 }}>
                {/* 점검 CRUD 카드 — onSchedulesChanged 로 캘린더 즉시 동기화 */}
                <Card padding={18}>
                    <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-content)', marginBottom: 4 }}>
                            점검 일정 관리
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--color-content-secondary)' }}>
                            등록된 점검 시간에는 잔고 캐싱 / 스냅샷 / 청산 알림이 자동 일시 중단됩니다.
                        </div>
                    </div>
                    <MaintenanceCard ref={maintenanceCardRef} onSchedulesChanged={onSchedulesChanged} />
                </Card>

                {/* 사이드: 거래소 빈도 + 자동화 규칙 placeholder */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <Card padding={18}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-content)' }}>
                                거래소별 점검 빈도
                            </div>
                            <span style={{
                                fontFamily: 'JetBrains Mono, monospace', fontSize: 10,
                                color: 'var(--color-content-muted)',
                            }}>
                                전체 기간
                            </span>
                        </div>
                        {freqData.length === 0 ? (
                            <div style={{ fontSize: 12, color: 'var(--color-content-muted)', padding: '16px 0' }}>
                                데이터 없음
                            </div>
                        ) : (
                            freqData.map((f) => (
                                <FreqRow key={f.ex} {...f} maxCount={maxCount} />
                            ))
                        )}
                    </Card>

                    {/* TODO(DAS-65-G3): 자동화 규칙 placeholder */}
                    <AutomationRulesPlaceholder />
                </div>
            </div>
        </div>
    );
}

/* ── 모바일 V2 레이아웃 ─────────────────────────────────── */
function MaintenanceMobileV2({ schedules, loading, calYear, calMonth, onPrevMonth, onNextMonth, onSchedulesChanged, onOpenCreateModal, maintenanceCardRef }) {
    const upcomingSchedules = schedules
        .filter((s) => classifySchedule(s) === 'upcoming')
        .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));

    const nextSchedule = upcomingSchedules[0] || null;
    const countdownMs = nextSchedule ? new Date(nextSchedule.start_at) - new Date() : 0;

    const scheduledCount = schedules.filter((s) => classifySchedule(s) === 'upcoming').length;
    const completedCount = schedules.filter((s) => classifySchedule(s) === 'past').length;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* 카운트다운 히어로 — 시안 V2 모바일 "다음 점검까지" */}
            <div
                style={{
                    padding: 18,
                    background: 'linear-gradient(135deg, var(--color-surface-card), var(--color-surface-elevated))',
                    border: '1px solid var(--color-surface-border)',
                    borderRadius: 16,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <KpiLabel>다음 점검까지</KpiLabel>
                    <span style={{
                        fontFamily: 'JetBrains Mono, monospace', fontSize: 10,
                        color: 'var(--color-content-muted)',
                    }}>
                        예정 {scheduledCount} · 완료 {completedCount}
                    </span>
                </div>
                <div style={{
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: 28, fontWeight: 600,
                    letterSpacing: '-0.02em',
                    color: 'var(--color-content)',
                }}>
                    {nextSchedule ? formatCountdown(countdownMs) : '예정 없음'}
                </div>
                {nextSchedule && (
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <ExchangeChip name={nextSchedule.exchange} />
                        <span style={{
                            fontFamily: 'JetBrains Mono, monospace', fontSize: 11,
                            color: 'var(--color-content-secondary)',
                        }}>
                            {toKSTDayjs(nextSchedule.start_at).format('MM-DD HH:mm')} → {toKSTDayjs(nextSchedule.end_at).format('HH:mm')}
                        </span>
                    </div>
                )}
            </div>

            {/* 미니 캘린더 */}
            <Card padding={14}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <div style={{
                        fontFamily: 'JetBrains Mono, monospace',
                        fontSize: 12, fontWeight: 600, color: 'var(--color-content)',
                    }}>
                        {calYear}-{String(calMonth).padStart(2, '0')}
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                        <button
                            type="button"
                            onClick={onPrevMonth}
                            className="rounded border border-surface-border px-1.5 py-0.5 text-xs text-content-muted hover:bg-surface-hover transition-colors"
                            aria-label="이전 달"
                        >
                            ‹
                        </button>
                        <button
                            type="button"
                            onClick={onNextMonth}
                            className="rounded border border-surface-border px-1.5 py-0.5 text-xs text-content-muted hover:bg-surface-hover transition-colors"
                            aria-label="다음 달"
                        >
                            ›
                        </button>
                    </div>
                </div>
                <MonthlyCalendar schedules={schedules} year={calYear} month={calMonth} />
            </Card>

            {/* 점검 CRUD 카드 */}
            <Card padding={14}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-content)', marginBottom: 10 }}>
                    점검 일정 관리
                </div>
                <MaintenanceCard ref={maintenanceCardRef} onSchedulesChanged={onSchedulesChanged} />
            </Card>

            {/* TODO(DAS-65-G3): 자동화 규칙 placeholder */}
            <AutomationRulesPlaceholder />
        </div>
    );
}

/* ── 메인 클래스 컴포넌트 ─────────────────────────────────── */
class MaintenancePageV3 extends React.Component {
    constructor(props) {
        super(props);
        const now = new Date();
        this.state = {
            collapsed: getNavCollapsed(),
            schedules: [],
            loading: true,
            calYear: now.getFullYear(),
            calMonth: now.getMonth() + 1,
            // viewport 변경 대응
            isMobile: isMobileViewport(),
        };
        this.pollTimer = null;
        this._isMounted = false;
        this._onResize = this._onResize.bind(this);
        this.maintenanceCardRef = React.createRef();
    }

    _onResize() {
        const m = isMobileViewport();
        if (m !== this.state.isMobile) this.setState({ isMobile: m });
    }

    componentDidMount() {
        this._isMounted = true;
        this.loadSchedules();
        window.addEventListener('resize', this._onResize);
    }

    componentWillUnmount() {
        this._isMounted = false;
        clearTimeout(this.pollTimer);
        window.removeEventListener('resize', this._onResize);
    }

    loadSchedules = async () => {
        this.setState({ loading: true });
        try {
            const res = await listSchedules();
            if (!this._isMounted) return;
            this.setState({
                schedules: Array.isArray(res.data) ? res.data : [],
                loading: false,
            });
        } catch (_e) {
            if (!this._isMounted) return;
            this.setState({ loading: false });
        }
        // 5분 주기 폴링 (마운트 상태일 때만 예약)
        if (this._isMounted) {
            this.pollTimer = setTimeout(this.loadSchedules, 5 * 60 * 1000);
        }
    };

    onCollapse = (collapsed) => {
        setNavCollapsed(collapsed);
        this.setState({ collapsed });
    };

    // MaintenanceCard CRUD 후 캘린더 즉시 동기화 (DAS-66 패턴)
    onSchedulesChanged = () => {
        this.loadSchedules();
    };

    onPrevMonth = () => {
        this.setState((prev) => {
            let m = prev.calMonth - 1;
            let y = prev.calYear;
            if (m < 1) { m = 12; y -= 1; }
            return { calYear: y, calMonth: m };
        });
    };

    onNextMonth = () => {
        this.setState((prev) => {
            let m = prev.calMonth + 1;
            let y = prev.calYear;
            if (m > 12) { m = 1; y += 1; }
            return { calYear: y, calMonth: m };
        });
    };

    render() {
        const { collapsed, schedules, loading, calYear, calMonth, isMobile } = this.state;

        const onOpenCreateModal = () => {
            this.maintenanceCardRef.current?.onOpenCreateModal();
        };

        const layoutProps = {
            schedules,
            loading,
            calYear,
            calMonth,
            onPrevMonth: this.onPrevMonth,
            onNextMonth: this.onNextMonth,
            onSchedulesChanged: this.onSchedulesChanged,
            onOpenCreateModal,
            maintenanceCardRef: this.maintenanceCardRef,
        };

        return (
            <div className="min-h-screen w-full bg-surface text-content sm:overflow-x-auto">
                <NavBar collapsed={collapsed} onCollapse={this.onCollapse} />
                <main
                    className={`min-w-0 w-full pt-12 pb-16 sm:pt-0 sm:pb-0 transition-[padding-left] duration-200 ${
                        collapsed ? 'sm:pl-[56px]' : 'sm:pl-[220px]'
                    }`}
                    style={{ marginTop: 'var(--maintenance-banner-h, 0px)' }}
                >
                    <MaintenanceBanner />
                    <div className="mx-3 my-3 min-w-0 sm:mx-6 sm:my-5">
                        {isMobile
                            ? <MaintenanceMobileV2 {...layoutProps} />
                            : <MaintenanceDesktopV2 {...layoutProps} />
                        }
                    </div>
                </main>
                <ScrollToTopButton />
            </div>
        );
    }
}

export default MaintenancePageV3;
